import type { TableColumns, TableTagMappings } from '@ohpen/enso-ui';
import { TFunction } from 'i18next';

import { ActionEntityType } from '@pmp/api';

import { ChangeLogEntry, ChangeLogFieldChange, ChangeLogValue, ChangeOrigin } from './interfaces';

/** Value-list names the drawer builds from the entries and hands to the Table. */
export const FIELD_LIST_NAME = 'changeLogField';
export const ORIGIN_LIST_NAME = 'changeLogOrigin';

/** One row per change. The fields it touched are in the expanded detail below it. */
export const columns: TableColumns<ChangeLogEntry> = [
  {
    title: 'lender.changeLog.labels.changedAt',
    dataIndex: 'changedAt',
    key: 'changedAt',
    width: 150,
    options: {
      format: { type: 'dateTimeLocal' },
      filter: 'date',
    },
  },
  {
    title: 'lender.changeLog.labels.changedBy',
    dataIndex: 'changedBy',
    key: 'changedBy',
    width: 230,
    ellipsis: true,
    options: {
      filter: 'search',
    },
  },
  {
    title: 'lender.changeLog.labels.origin',
    dataIndex: 'origin',
    key: 'origin',
    width: 130,
    options: {
      format: { type: 'tag' },
      filter: 'select',
      listName: ORIGIN_LIST_NAME,
    },
  },
  {
    // Answers "when was the phone last changed" without opening every row: the filter keeps only the
    // changes that touched the chosen field.
    title: 'lender.changeLog.labels.fields',
    dataIndex: 'fields',
    key: 'fields',
    options: {
      format: { type: 'multiValueList', ellipsis: { visibleItems: 3 } },
      filter: 'select',
      listName: FIELD_LIST_NAME,
    },
  },
  {
    title: 'lender.changeLog.labels.changeRequest',
    dataIndex: 'changeRequestId',
    key: 'changeRequestId',
    width: 135,
  },
];

/**
 * The expanded detail of one change: one row per field that moved, with what it held on either side.
 * A collection's table adds a leading column naming the row of it the field belongs to, and is
 * otherwise read exactly like the record's own, so the whole panel reads in one direction.
 */
export const getExpandedColumns = (
  emptyLabel: string,
  fieldLabel: (field: string) => string,
  valueLabel: (field: string, value: ChangeLogValue) => string | null,
  itemColumn?: { title: string; field: string },
): TableColumns<ChangeLogFieldChange> => [
  ...(itemColumn
    ? [
        {
          title: itemColumn.title,
          dataIndex: 'item',
          key: 'item',
          width: 200,
          render: (item: unknown) => valueLabel(itemColumn.field, item as ChangeLogValue),
        },
      ]
    : []),
  {
    title: 'lender.changeLog.labels.field',
    dataIndex: 'field',
    key: 'field',
    width: 220,
    render: (_: unknown, change: ChangeLogFieldChange) => fieldLabel(change.field),
  },
  {
    title: 'lender.changeLog.labels.previousValue',
    dataIndex: 'previousValue',
    key: 'previousValue',
    width: 300,
    render: (value: unknown, record: ChangeLogFieldChange) => valueLabel(record.field, value as ChangeLogValue) ?? emptyLabel,
  },
  {
    title: 'lender.changeLog.labels.newValue',
    dataIndex: 'newValue',
    key: 'newValue',
    width: 300,
    render: (value: unknown, record: ChangeLogFieldChange) => valueLabel(record.field, value as ChangeLogValue) ?? emptyLabel,
  },
];

export const getOriginTagMappings = (): TableTagMappings => ({
  origin: {
    [ChangeOrigin.PORTAL]: { color: '' },
    [ChangeOrigin.BROKER_PORTAL]: { color: 'blue' },
    [ChangeOrigin.FCA]: { color: 'orange' },
  },
});

/** Entities managed in BMP. Everything else in the change log is managed in PMP. */
const BMP_ENTITIES = new Set<string>([
  ActionEntityType.BROKERS,
  ActionEntityType.FIRMS,
  ActionEntityType.PROCURATION_FEES,
  ActionEntityType.PROCURATION_FEE_RULES,
  ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS,
  ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS,
]);

/**
 * Origin labels for one entity. A change made in the portal is labelled with the portal it was made
 * in, because "Portal" alone says nothing to a reader who works in both.
 */
export const getOriginList = (t: TFunction, entityType: string): Record<string, string> => ({
  [ChangeOrigin.PORTAL]: BMP_ENTITIES.has(entityType) ? t('lender.changeLog.origins.BMP') : t('lender.changeLog.origins.PMP'),
  [ChangeOrigin.BROKER_PORTAL]: t('lender.changeLog.origins.BROKER_PORTAL'),
  [ChangeOrigin.FCA]: t('lender.changeLog.origins.FCA'),
});
